import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, viewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { NgbDropdown, NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Options } from '@popperjs/core';
import { MainMenuStateService } from '@services/main-menu-state.service';
import { NavLayoutService } from '@services/nav-layout.service';
import { ThemeService } from '@services/theme.service';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { SidepanelEventsService } from 'mbs-ui-kit/sidepanel';
import { AppHeaderBaseComponent } from 'mbs-ui-shared@components/app-header/app-header-base-component';
import { MenuItemType } from 'mbs-ui-shared@components/app-header/app-header.types';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { AppEnvStateService } from 'mbs-ui-shared@services/app-env-state.service';
import {
  sideNavChildOrder,
  sideNavElementSelectors,
  sideNavFlyoutDistance,
  sideNavSeparatorBeforeIds,
  sideNavTopLevelOrder
} from './app-side-nav.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-side-nav',
  templateUrl: './app-side-nav.component.html',
  styleUrls: ['./app-side-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, NgbDropdownModule, NgbTooltipModule, I18NextPipe]
})
export class AppSideNavComponent extends AppHeaderBaseComponent {
  private ability = inject(AbilityService);
  private auth = inject(AuthFacade);
  private menuState = inject(MainMenuStateService);
  private navLayout = inject(NavLayoutService);
  private sidepanelEvents = inject(SidepanelEventsService);
  private themeService = inject(ThemeService);
  private appEnvStateService = inject(AppEnvStateService);
  protected router = inject(Router);

  public readonly elementSelectors = sideNavElementSelectors;
  protected readonly appEnvState = this.appEnvStateService.appEnvState;
  private readonly brandingSetting = toSignal(this.auth.brandingSetting$);
  protected readonly isRebranded = computed(() => !!this.brandingSetting()?.BrandingActivated);
  protected readonly logoUrl = computed(() => {
    if (this.isRebranded()) {
      return this.brandingSetting().FooterLogoImageUrl;
    }

    return this.themeService.isDark() ? 'assets/vmenu-top-left-logo-dark.svg' : 'assets/vmenu-top-left-logo-light.svg';
  });
  protected readonly menu = computed<MenuItemType[]>(() =>
    this.sortByOrder([...this.menuState.overviewMenu(), ...this.menuState.mainMenu()], sideNavTopLevelOrder).map((item) =>
      sideNavChildOrder[item.id] && item.children
        ? { ...item, children: this.sortByOrder(item.children, sideNavChildOrder[item.id]) }
        : item
    )
  );
  protected readonly dot = this.menuState.dot;
  protected readonly collapsed = this.navLayout.collapsed;
  protected readonly flyoutPopperOptions = (options: Partial<Options>): Partial<Options> => ({
    ...options,
    modifiers: [...options.modifiers, { name: 'offset', options: { offset: [0, sideNavFlyoutDistance] } }]
  });
  private readonly flyouts = viewChildren(NgbDropdown);

  private groupExpansion = linkedSignal<string[], Record<string, boolean>>({
    source: () => this.getActiveGroupIds(this.menu()),
    computation: (activeGroupIds, previous) => ({
      ...Object.fromEntries(activeGroupIds.map((id) => [id, true])),
      ...previous?.value
    })
  });

  constructor() {
    super();

    this.initStreams();

    this.sidepanelEvents.onFullWidth.pipe(untilDestroyed(this)).subscribe(() => this.navLayout.setCollapsed(true));
  }

  protected get homePageUrl(): string {
    return this.ability.can('read', 'Dashboard') ? RoutingPath.ApDashboard : RoutingPath.ApSettings;
  }

  protected hasSeparatorBefore(item: MenuItemType): boolean {
    return sideNavSeparatorBeforeIds.includes(item.id);
  }

  protected isGroupActive(item: MenuItemType): boolean {
    return !!this.getActiveChild(item);
  }

  protected getGroupTooltip(item: MenuItemType): string {
    const activeChild = this.getActiveChild(item);

    return activeChild ? `${item.name} • ${activeChild.name}` : item.name;
  }

  protected isExpanded(item: MenuItemType): boolean {
    return !!this.groupExpansion()[item.id];
  }

  protected handleToggleGroup(item: MenuItemType): void {
    this.groupExpansion.update((expansion) => ({ ...expansion, [item.id]: !expansion[item.id] }));
  }

  protected handleToggleCollapse(): void {
    this.navLayout.toggleCollapsed();
  }

  protected handleItemClick(item: MenuItemType): void {
    this.menuState.hideDot(item.dotType);
    this.flyouts().forEach((flyout) => flyout.close());
  }

  private sortByOrder(items: MenuItemType[], order: string[]): MenuItemType[] {
    const position = ({ id }: MenuItemType) => (order.includes(id) ? order.indexOf(id) : order.length);

    return items.toSorted((a, b) => position(a) - position(b));
  }

  private getActiveGroupIds(items: MenuItemType[]): string[] {
    return items
      .filter((item) => item.children?.length > 0 && this.isGroupActive(item))
      .flatMap((item) => [item.id, ...this.getActiveGroupIds(item.children)]);
  }
}
